// ✅ src/pages/VehicleV2/VehicleTypeMaintenanceEntry.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { FaArrowLeft, FaSave, FaUndo } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/maintenanceEntry";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const VehicleTypeMaintenanceEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage values like your new pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const ipAddress = useMemo(() => sessionStorage.getItem("ipAddress") || "", []);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // dropdowns
  const [vehicleSubTypes, setVehicleSubTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState([]);

  // Equipment list
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

  // loading
  const [loading, setLoading] = useState(false);

  // form
  const [form, setForm] = useState({
    storeId: "",
    subTypeId: "",
    qty: "",
    unit: "",
    vehicleBasisId: "",
    dateOfReceipt: "",
    specification: "",
    subAssemblyName: "",
    partNumber: "",
    description: "",
    usageLimit: "",
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const cardClass = `p-6 rounded-xl border ${
    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => {
    setForm({
      storeId: "",
      subTypeId: "",
      qty: "",
      unit: "",
      vehicleBasisId: "",
      dateOfReceipt: "",
      specification: "",
      subAssemblyName: "",
      partNumber: "",
      description: "",
      usageLimit: "",
    });
    setVehicles([]);
    setSelectedVehicleIds([]);
  };

  // ✅ load dropdowns
  const loadDropdowns = async () => {
    const res = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId },
      headers: commonHeaders,
    });

    if (res.data?.success) {
      setVehicleSubTypes(Array.isArray(res.data?.data?.vehicleSubTypes) ? res.data.data.vehicleSubTypes : []);
      setStores(Array.isArray(res.data?.data?.stores) ? res.data.data.stores : []);
      setVehicleBasis(Array.isArray(res.data?.data?.vehicleBasis) ? res.data.data.vehicleBasis : []);
    } else {
      throw new Error(res.data?.message || "Dropdown load failed");
    }
  };

  // ✅ load equipment list
  const loadVehicles = async () => {
    const subTypeId = form.subTypeId;
    const basisId = form.vehicleBasisId;
    const subAssemblyName = (form.subAssemblyName || "").trim();

    if (!subTypeId || !basisId || !subAssemblyName) {
      setVehicles([]);
      setSelectedVehicleIds([]);
      return;
    }

    const res = await axios.get(`${API_BASE}/vehicles`, {
      params: {
        branchId,
        subTypeId,
        basisId,
        subAssemblyName,
      },
      headers: commonHeaders,
    });

    if (res.data?.success) {
      setVehicles(Array.isArray(res.data?.data) ? res.data.data : []);
      setSelectedVehicleIds([]);
    } else {
      toast.error(res.data?.message || "Vehicle list load failed");
      setVehicles([]);
      setSelectedVehicleIds([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadDropdowns();
      } catch (e) {
        toast.error(e?.message || "Dropdown load failed");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  useEffect(() => {
    loadVehicles().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subTypeId, form.vehicleBasisId, form.subAssemblyName]);

  const toggleVehicle = (id) => {
    setSelectedVehicleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const validate = () => {
    if (!form.storeId) return "Select Store";
    if (!form.subTypeId) return "Select Sub Type";
    if (!form.qty || Number(form.qty) <= 0) return "Enter valid Qty";
    if (!form.vehicleBasisId) return "Select Vehicle Basis";
    if (!form.dateOfReceipt) return "Select Date of Receipt";
    if (!form.subAssemblyName.trim()) return "Enter Sub Assembly";
    if (!form.partNumber.trim()) return "Enter PartNumber/Grade";
    if (!form.description.trim()) return "Enter Description";
    if (!selectedVehicleIds.length) return "Select Equipment List";
    if (form.usageLimit === "" || Number(form.usageLimit) <= 0) return "Enter valid Usage Limit";
    return "";
  };

  const handleSubmit = async () => {
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        branchId: toInt(branchId),
        loginUserId: toInt(userId),
        storeId: toInt(form.storeId),
        subTypeId: toInt(form.subTypeId),
        vehicleBasisId: toInt(form.vehicleBasisId),
        subAssemblyName: form.subAssemblyName.trim(),
        partNumber: form.partNumber.trim(),
        qty: Number(form.qty),
        unit: form.unit || "",
        description: form.description.trim(),
        specification: form.specification || "",
        usageLimit: Number(form.usageLimit),
        dateOfReceipt: form.dateOfReceipt || "",
        ipAddress,
        vehicleIds: selectedVehicleIds,
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Inserted Successfully");
        resetForm();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    resetForm();
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE MAINTENANCE ENTRY</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT */}
          <div className={cardClass}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Store<span className="text-red-500">*</span>
                </label>
                <select name="storeId" value={form.storeId} onChange={onChange} className={inputClass}>
                  <option value="">Select</option>
                  {stores.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Sub Type<span className="text-red-500">*</span>
                </label>
                <select name="subTypeId" value={form.subTypeId} onChange={onChange} className={inputClass}>
                  <option value="">Select</option>
                  {vehicleSubTypes.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Qty<span className="text-red-500">*</span>
                </label>
                <input type="number" name="qty" value={form.qty} onChange={onChange} className={inputClass} />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Unit</label>
                <input type="text" name="unit" value={form.unit} onChange={onChange} className={inputClass} />
              </div>

              <div className="md:col-span-2 mt-6">
                <label className={labelClass}>
                  Vehicle Basis<span className="text-red-500">*</span>
                </label>
                <select
                  name="vehicleBasisId"
                  value={form.vehicleBasisId}
                  onChange={onChange}
                  className={inputClass}
                >
                  <option value="">Select</option>
                  {vehicleBasis.map((x, idx) => (
                    <option key={x.id ?? idx} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Date of Receipt<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dateOfReceipt"
                  value={form.dateOfReceipt}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Specification</label>
                <input
                  type="text"
                  name="specification"
                  value={form.specification}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className={cardClass}>
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className={labelClass}>
                  Sub Assembly<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subAssemblyName"
                  value={form.subAssemblyName}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  PartNumber Grade<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="partNumber"
                  value={form.partNumber}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Description<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  Equipment List<span className="text-red-500">*</span>
                </label>

                <div
                  className={`h-40 overflow-auto rounded-lg border p-2 ${
                    isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
                  }`}
                >
                  {!form.subTypeId || !form.vehicleBasisId || !form.subAssemblyName.trim() ? (
                    <div className="text-sm opacity-70 p-2">
                      Select Sub Type + Vehicle Basis and enter Sub Assembly to load equipment list.
                    </div>
                  ) : vehicles.length === 0 ? (
                    <div className="text-sm opacity-70 p-2">{loading ? "Loading..." : "No vehicles found"}</div>
                  ) : (
                    vehicles.map((v, idx) => {
                      const vehicleId =
                        v.id ??
                        v.vehicleId ??
                        v.fld_vehicleid ??
                        v.fld_vehiclemasterid ??
                        idx;

                      return (
                        <label
                          key={vehicleId}
                          className={`flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer ${
                            isDark ? "hover:bg-gray-800" : "hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedVehicleIds.includes(vehicleId)}
                            onChange={() => toggleVehicle(vehicleId)}
                          />
                          <span className="text-sm">
                            {v.regNo || v.registrationNo || v.fld_regno || "-"}{" "}
                            <span className="opacity-70">
                              ({v.equipmentSlNo || v.equipmentNo || v.fld_equipmentslno || "-"})
                            </span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="mt-3">
                <label className={labelClass}>
                  Usage Limit<span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="usageLimit"
                  value={form.usageLimit}
                  onChange={onChange}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            disabled={loading}
            onClick={handleSubmit}
            className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
              loading ? "opacity-70 cursor-not-allowed" : ""
            } ${buttonClass} ${hoverClass}`}
          >
            <FaSave className="w-4 h-4" />
            Submit
          </button>

          <button
            disabled={loading}
            onClick={handleReset}
            className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
              isDark
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            } ${loading ? "opacity-70 cursor-not-allowed" : ""}`}
          >
            <FaUndo className="w-4 h-4" />
            Reset
          </button>
        </div>

        <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="text-xs opacity-70">{loading ? "Saving / Loading..." : ""}</div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaintenanceEntry;